/**
 * Map style types and helpers for FlowRide frontend.
 * Contains style ids, UI preferences, and style resolution logic.
 */

/** MapTiler API key */
export const MAPTILER_KEY = "VL8sEvEOFIaN4AiGM6xC";

/** Available MapTiler style identifiers */
export type MapStyleId = "streets-v4" | "streets-v4-dark" | "hybrid" | "openstreetmap";

/** User-facing map appearance mode */
export type MapAppearanceMode = "light" | "dark" | "auto";

/** Optional style override independent from appearance mode */
export type MapStyleOverride = "default" | "hybrid" | "legacy";

/** Default appearance mode */
export const DEFAULT_MAP_APPEARANCE_MODE: MapAppearanceMode = "light";

/** Default map override */
export const DEFAULT_MAP_STYLE_OVERRIDE: MapStyleOverride = "default";

/** Available style override targets */
export const MAP_STYLE_OVERRIDE_TO_ID: Record<MapStyleOverride, MapStyleId> = {
    "default": "streets-v4",
    "hybrid":  "hybrid",
    "legacy":  "openstreetmap",
};

/** Icon paths for mode buttons */
export const MAP_APPEARANCE_MODE_ICONS: Record<MapAppearanceMode, string> = {
    "light": "/assets/icon/map_prefs/map_styles/streets-v4.png",
    "dark":  "/assets/icon/map_prefs/map_styles/streets-v2-dark.png",
    "auto":  "/assets/icon/map_prefs/map_styles/autoDark.png",
};

/** Icon paths for style override buttons */
export const MAP_STYLE_OVERRIDE_ICONS: Record<MapStyleOverride, string> = {
    "default": "/assets/icon/map_prefs/map_styles/streets-v4.png",
    "hybrid":  "/assets/icon/map_prefs/map_styles/hybrid.png",
    "legacy":  "/assets/icon/map_prefs/map_styles/openstreetmap.png",
};

export const MAP_STYLE_ID_ICONS: Record<MapStyleId, string> = {
    "streets-v4":      "/assets/icon/map_prefs/map_styles/streets-v4.png",
    "streets-v4-dark": "/assets/icon/map_prefs/map_styles/streets-v2-dark.png",
    "hybrid":          "/assets/icon/map_prefs/map_styles/hybrid.png",
    "openstreetmap":   "/assets/icon/map_prefs/map_styles/openstreetmap.png",
};

export const MAP_STYLE_ID_MAPTILER_ID: Record<MapStyleId, string> = {
    "streets-v4":      "019e49f1-8d51-7476-8195-77042cf010ed",
    "streets-v4-dark": "019e49fc-cd8e-701c-8dfa-2356a839f5c5",
    "hybrid":          "019e4a73-0ba5-76d5-b63e-2b2a48a0cd46",
    "openstreetmap":   "019e4a03-b1ca-778d-a0d2-08bff13e2e15",
};

/** Build the MapTiler style JSON URL for a given style */
export const getMapStyleUrl = ( styleId: MapStyleId ): string => {
    return `https://api.maptiler.com/maps/${ MAP_STYLE_ID_MAPTILER_ID[styleId] }/style.json?key=${ MAPTILER_KEY }`;
};

/** Build the MapTiler raster tile JSON URL for a given style (navigation mode) */
export const getMapTileUrl = ( styleId: MapStyleId ): string => {
    return `https://api.maptiler.com/maps/${ MAP_STYLE_ID_MAPTILER_ID[styleId] }/tiles.json?key=${ MAPTILER_KEY }`;
};

/** Build a static map preview URL for the style selector */
export const getMapPreviewUrl = ( styleId: MapStyleId ): string => {
    return `https://cloud.maptiler.com/static/img/maps/${ MAP_STYLE_ID_MAPTILER_ID[styleId] }.png`;
};

/**
 * Resolve the active map style from mode + override + system preference.
 */
export const resolveActiveMapStyleId = (
    mode: MapAppearanceMode,
    styleOverride: MapStyleOverride,
    systemPrefersDark: boolean,
): MapStyleId => {
    if ( styleOverride === "hybrid" ) {
        return "hybrid";
    }

    if ( styleOverride === "legacy" ) {
        return "openstreetmap";
    }

    if ( mode === "auto" ) {
        return systemPrefersDark ? "streets-v4-dark" : "streets-v4";
    }

    return mode === "dark" ? "streets-v4-dark" : "streets-v4";
};
